/**
 * Analytics Constants - Enums and Whitelists
 * 
 * IMPORTANT: All values here are strict enums.
 * Never use free-form strings in analytics events.
 */

// =============================================================================
// EVENT NAMES
// =============================================================================

export const EVENTS = {
  // Acquisition
  LANDING_VIEWED: 'Landing Viewed',
  CTA_CLICKED: 'CTA Clicked',
  
  // Wizard
  WIZARD_STARTED: 'Wizard Started',
  MEAL_SET_REQUESTED: 'Meal Set Requested',
  
  // Generation
  MEAL_SET_GENERATION_SUCCEEDED: 'Meal Set Generation Succeeded',
  MEAL_SET_GENERATION_FAILED: 'Meal Set Generation Failed',
  MEAL_PLAN_STRUCTURE_INVALID: 'Meal Plan Structure Invalid',
  
  // Core Value: Recipes
  RECIPE_VIEWED: 'Recipe Viewed',
  RECIPE_REPLACED: 'Recipe Replaced',
  
  // Core Value: Grocery
  GROCERY_LIST_VIEWED: 'Grocery List Viewed',
  GROCERY_ITEM_TOGGLED: 'Grocery Item Toggled',
  
  // Conversion
  SAVE_CLICKED: 'Save Clicked',
  AUTH_STARTED: 'Auth Started',
  AUTH_COMPLETED: 'Auth Completed',
  MEAL_SET_SAVED: 'Meal Set Saved',
  
  // Limits & Paywall
  LIMIT_REACHED: 'Limit Reached',
  PAYWALL_VIEWED: 'Paywall Viewed',
  
  // Retention
  MY_MEALS_VIEWED: 'My Meals Viewed',
  SAVED_MEAL_SET_OPENED: 'Saved Meal Set Opened',

  // Promo Landing
  PROMO_LANDING_VIEWED: 'Promo Landing Viewed',
  PROMO_CTA_CLICKED: 'Promo CTA Clicked',
};

// =============================================================================
// ENUMS - Strict value sets
// =============================================================================

export const CTA_LOCATION = {
  HERO: 'hero',
  STICKY: 'sticky',
  FOOTER: 'footer',
  HEADER: 'header',
  OTHER: 'other',
};

export const ENTRY_SOURCE = {
  LANDING_CTA: 'landing_cta',
  DIRECT: 'direct',
  HEADER: 'header',
  SAVED_LINK: 'saved_link',
  PROMO_LANDING: 'promo_landing',
  OTHER: 'other',
};

export const BUDGET_CATEGORY = {
  BUDGET: 'budget',
  STANDARD: 'standard',
  GOURMET: 'gourmet',
};

export const MEAL_TYPE = {
  BREAKFAST: 'breakfast',
  LUNCH: 'lunch',
  DINNER: 'dinner',
};

export const ERROR_STAGE = {
  PROMPT: 'prompt',
  API: 'api',
  PARSE: 'parse',
  TIMEOUT: 'timeout',
  UNKNOWN: 'unknown',
};

export const ERROR_CODE = {
  RATE_LIMIT: 'rate_limit',
  INVALID_JSON: 'invalid_json',
  NETWORK: 'network',
  SERVER_5XX: 'server_5xx',
  CLIENT_4XX: 'client_4xx',
  TIMEOUT: 'timeout',
  UNKNOWN: 'unknown',
};

export const TOGGLE_STATE = {
  ON: 'on',
  OFF: 'off',
};

export const OPEN_TRIGGER = {
  BUTTON: 'button',
  AUTO: 'auto',
};

export const AUTH_INTENT = {
  SAVE_PLAN: 'save_plan',
  LOGIN: 'login',
};

export const AUTH_METHOD = {
  EMAIL: 'email',
  GOOGLE: 'google',
  APPLE: 'apple',
};

export const LIMIT_TYPE = {
  GENERATIONS: 'generations',
  REPLACEMENTS: 'replacements',
};

export const PAYWALL_TRIGGER = {
  GENERATION_LIMIT: 'generation_limit',
  REPLACEMENT_LIMIT: 'replacement_limit',
  MANUAL: 'manual',
};

export const PAYWALL_TYPE = {
  TRIES_DRAWER: 'tries_drawer',
  PREMIUM_PROMO: 'premium_promo',
  REPLACEMENT_DRAWER: 'replacement_drawer',
  OTHER: 'other',
};

export const CONTEXT_ACTION = {
  GENERATE: 'generate',
  REPLACE: 'replace',
  SAVE: 'save',
  OPEN_GROCERY: 'open_grocery',
  OPEN_RECIPE: 'open_recipe',
  OTHER: 'other',
};

// =============================================================================
// PRODUCT CATEGORIES - Aligned with groceryConstants.js CATEGORIES
// =============================================================================

export const PRODUCT_CATEGORY = {
  MEAT_SEAFOOD: 'Meat & Seafood',
  PRODUCE: 'Produce',
  DAIRY_EGGS: 'Dairy, Alternatives & Eggs',
  GRAINS_PASTA: 'Grains & Pasta',
  FROZEN: 'Frozen',
  BAKERY: 'Bakery',
  REFRIGERATED: 'Refrigerated',
  PANTRY: 'Pantry',
  CONDIMENTS: 'Condiments',
  OTHER: 'Other',
};

// =============================================================================
// CUISINE WHITELIST (for primary_cuisine)
// =============================================================================

export const CUISINE = {
  ITALIAN: 'Italian',
  MEXICAN: 'Mexican',
  ASIAN: 'Asian',
  MEDITERRANEAN: 'Mediterranean',
  AMERICAN: 'American',
  INDIAN: 'Indian',
  FRENCH: 'French',
  JAPANESE: 'Japanese',
  CHINESE: 'Chinese',
  THAI: 'Thai',
  GREEK: 'Greek',
  SPANISH: 'Spanish',
  MIDDLE_EASTERN: 'Middle Eastern',
  KOREAN: 'Korean',
  VIETNAMESE: 'Vietnamese',
  SURPRISE_ME: 'Surprise Me',
  OTHER: 'Other',
};

// =============================================================================
// DIETARY FLAGS WHITELIST
// =============================================================================

export const DIETARY_FLAG = {
  VEGETARIAN: 'vegetarian',
  VEGAN: 'vegan',
  GLUTEN_FREE: 'gluten_free',
  DAIRY_FREE: 'dairy_free',
  NUT_FREE: 'nut_free',
  LOW_CARB: 'low_carb',
  KETO: 'keto',
  HALAL: 'halal',
  KOSHER: 'kosher',
  PESCATARIAN: 'pescatarian',
};

// =============================================================================
// STORAGE KEYS
// =============================================================================

export const STORAGE_KEYS = {
  FLOW_ID: 'amplitude_flow_id',
  FLOW_ID_TIMESTAMP: 'amplitude_flow_id_ts',
  GENERATION_ID: 'amplitude_generation_id',
  MEAL_SET_ID: 'amplitude_meal_set_id',
};

// Flow ID TTL: 24 hours
export const FLOW_ID_TTL_MS = 24 * 60 * 60 * 1000;
